import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { Store } from '@ngrx/store';

import { Observable, of, ReplaySubject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import { AppState, Selectors, Actions } from 'store/root-reducer';
import { TableNameType, TablePreferenceType, TablesPreference, TableSortPreference } from 'modules/shared/models/table-preference.models';
import { PeriodicReviewResponse, PeriodicReview } from 'modules/shared/models/periodic-review.models';

import { CustomerService } from 'modules/core/services/customer.service';
import { SnackbarService } from 'app/services/snackbar.service';

@Component({
  selector: 'artemis-periodic-review-table',
  templateUrl: './periodic-review-table.component.html',
  styleUrls: ['./periodic-review-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PeriodicReviewTableComponent implements OnInit, OnDestroy {

  @ViewChild(MatSort, {static: true}) set sort(sort: MatSort) {
    this.periodicReviewDataSource.sort = sort;
  }

  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  // Names should be same as used for sorting on the backend
  readonly displayedColumns: Array<string> = ['name', 'risk_rating', 'risk_report_created_at', 'actions'];
  readonly pageSizeOptions: Array<number> = [5, 10, 15];
  currentPage: number = 0;
  currentTableSort: string;

  preferredPeriodicReviewTablePageSize: number = this.pageSizeOptions[0];
  periodicReviewDataSourceLength: Observable<number>;

  isLoading: boolean = false;

  filterPeriodicReviewName: string;
  periodicReviewResults: Array<PeriodicReview>;
  periodicReviewDataSource: MatTableDataSource<PeriodicReview> = new MatTableDataSource([]);

  isActiveTableTab: string = 'context';

  constructor(private customersService: CustomerService,
    private snackBar: SnackbarService,
    private cdRef: ChangeDetectorRef,
    private store: Store<AppState>) { }

  ngOnInit() {
    this.getPeriodicReviewTablePreference();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  /**
   * Get table pagination preference from store
   * @method getPeriodicReviewTablePreference
   */
  private getPeriodicReviewTablePreference(): void {
    this.store.select(Selectors.userPreference.getTablesPreference)
      .pipe(
        takeUntil(this.unsubscribe$),
        take(1)
      )
      .subscribe((tablesPreference: TablesPreference) => {
        const tablePreference = tablesPreference[TableNameType.ONGOING_DUE_DILIGENCE_TABLE];

        if (tablePreference) {
          if (tablePreference[TablePreferenceType.PAGE_SIZE]) {
            this.preferredPeriodicReviewTablePageSize = tablePreference[TablePreferenceType.PAGE_SIZE];
          }

          if (tablePreference[TablePreferenceType.SORT]) {
            this.periodicReviewDataSource.sort.active = tablePreference[TablePreferenceType.SORT].active;
            this.periodicReviewDataSource.sort.direction = tablePreference[TablePreferenceType.SORT].direction;
          }
        }

        this.getPeriodicReviewTableData();
      });
  }

  /**
   * Load list of periodic review
   * @method getPeriodicReviewTableData
   */
  getPeriodicReviewTableData(): void {
    this.isLoading = true;
    this.cdRef.detectChanges();

    this.customersService
      .loadPeriodicReview({
        limit: this.preferredPeriodicReviewTablePageSize,
        offset: this.preferredPeriodicReviewTablePageSize * this.currentPage,
        sort: this.currentTableSort,
        name: this.filterPeriodicReviewName
      })
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (res: PeriodicReviewResponse) => {
          this.periodicReviewResults = res.results.map((periodicReview: PeriodicReview) => {
            const output = Object.assign({}, periodicReview);
            output.riskReportCreatedAt = output.riskReportCreatedAt.replace(/\s/g, 'T');
            return output;
          });
          this.periodicReviewDataSourceLength = of(res.count);
          this.periodicReviewDataSource.data = this.periodicReviewResults;
          this.isLoading = false;
          this.cdRef.detectChanges();
        },
        (error: any) => {
          this.snackBar.error('Error: Failed to load list of periodic review');
          this.isLoading = false;
          this.cdRef.detectChanges();
        }
      );
  }

  /**
   * Pagination event handler
   * @method tablePaginationChangeHandler
   */
  tablePaginationChangeHandler(event: PageEvent): void {
    if (event.pageSize !== this.preferredPeriodicReviewTablePageSize) {
      this.store.dispatch(new Actions.userPreference.SetPaginationPreference(
        {tableName: TableNameType.ONGOING_DUE_DILIGENCE_TABLE, pageSize: event.pageSize}
      ));
      this.preferredPeriodicReviewTablePageSize = event.pageSize;
    }
    this.currentPage = event.pageIndex;
    this.getPeriodicReviewTableData();
  }

  /**
   * Add sort information to preference store
   * @method tableSortChangeHandler
   */
  tableSortChangeHandler(event: Sort): void {
    this.store.dispatch(new Actions.userPreference.SetSortPreference({
      tableName: TableNameType.ONGOING_DUE_DILIGENCE_TABLE,
      [TablePreferenceType.SORT]: <TableSortPreference>{
        active: event.active,
        direction: event.direction
      }
    }));
    switch (event.direction) {
        case 'asc':
          this.currentTableSort = `${event.active}`;
          break;
        case 'desc':
          this.currentTableSort = `-${event.active}`;
          break;
        default:
          this.currentTableSort = undefined;
          break;
      }
      this.getPeriodicReviewTableData();
  }

  /**
   * Apply filter by name for the table
   * @method applyNameFilter
   */
  applyNameFilter(filterValue: string): void {
    this.currentPage = 0;
    this.paginator.firstPage();
    this.getPeriodicReviewTableData();
  }
}
